import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { resolveBackendUrl } from '../services/api';

function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaDisplay, setCaptchaDisplay] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Tenant logo state
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=enter details, 2=enter OTP, 3=new password
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetId, setResetId] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  // Load tenants with logos. Restore last-used tenant from localStorage so
  // returning users see their institute logo immediately.
  useEffect(() => {
    async function loadTenants() {
      try {
        const response = await api.get('/auth/logo');
        if (response.data.success && response.data.data.length > 0) {
          const list = response.data.data;
          setTenants(list);
          const lastId = parseInt(localStorage.getItem('qposm_last_tenant_id'), 10);
          const remembered = lastId ? list.find((t) => t.TenantID === lastId) : null;
          setSelectedTenant(remembered || list[0]);
        }
      } catch {
        // silent — logos are optional
      }
    }
    loadTenants();
  }, []);

  const loadCaptcha = useCallback(async () => {
    try {
      const response = await api.get('/auth/captcha');
      if (response.data.success) {
        setCaptchaId(response.data.captchaId);
        setCaptchaDisplay(response.data.captcha.display);
        setCaptchaAnswer('');
      }
    } catch {
      setError('Failed to load security check. Is the server running?');
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Please enter username and password');
      return;
    }
    if (!captchaAnswer) {
      setError('Please answer the security check');
      return;
    }

    setLoading(true);
    try {
      const result = await login(username, password, captchaId, captchaAnswer);
      if (result.success) {
        // Remember tenant for next visit so the login page can show their logo.
        const tid = result.user?.tenantId;
        if (tid) localStorage.setItem('qposm_last_tenant_id', String(tid));
      } else {
        setError(result.message || 'Login failed');
        loadCaptcha();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed. Please try again.';
      setError(msg);
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // Forgot password handlers
  const handleForgotStep1 = async (e) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotUsername || !forgotEmail) {
      setForgotError('Please enter both username and email');
      return;
    }
    setForgotLoading(true);
    try {
      const response = await api.post('/auth/forgot-password', { username: forgotUsername, email: forgotEmail });
      if (response.data.success) {
        setResetId(response.data.resetId);
        setForgotMessage('A verification code has been sent to your email.');
        setForgotStep(2);
      }
    } catch (err) {
      setForgotError(err.response?.data?.message || 'Failed to process request');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotStep2 = async (e) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotOtp) {
      setForgotError('Please enter the verification code');
      return;
    }
    setForgotLoading(true);
    try {
      const response = await api.post('/auth/forgot-password/verify', { resetId, otp: forgotOtp });
      if (response.data.success) {
        setForgotMessage('Code verified. Please set your new password.');
        setForgotStep(3);
      }
    } catch (err) {
      setForgotError(err.response?.data?.message || 'Verification failed');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotStep3 = async (e) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotNewPassword || !forgotConfirmPassword) {
      setForgotError('Please fill all fields');
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError('Passwords do not match');
      return;
    }
    setForgotLoading(true);
    try {
      const response = await api.post('/auth/forgot-password/reset', { resetId, newPassword: forgotNewPassword });
      if (response.data.success) {
        setForgotMessage('Password reset successfully! You can now login.');
        setForgotError('');
        setTimeout(() => {
          resetForgotState();
        }, 2500);
      }
    } catch (err) {
      setForgotError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotState = () => {
    setShowForgot(false);
    setForgotStep(1);
    setForgotUsername('');
    setForgotEmail('');
    setResetId('');
    setForgotOtp('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotMessage('');
    setForgotError('');
  };

  // =============================================
  // FORGOT PASSWORD MODAL
  // =============================================
  if (showForgot) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ maxWidth: 440 }}>
          <div className="login-header">
            <h1>QPOSM</h1>
            <p>Forgot Password</p>
          </div>

          {/* Step indicator */}
          <div className="forgot-steps">
            <div className={`forgot-step ${forgotStep >= 1 ? 'active' : ''} ${forgotStep > 1 ? 'done' : ''}`}>
              <span className="step-num">{forgotStep > 1 ? '✓' : '1'}</span>
              <span className="step-label">Identity</span>
            </div>
            <div className="step-line" />
            <div className={`forgot-step ${forgotStep >= 2 ? 'active' : ''} ${forgotStep > 2 ? 'done' : ''}`}>
              <span className="step-num">{forgotStep > 2 ? '✓' : '2'}</span>
              <span className="step-label">Verify</span>
            </div>
            <div className="step-line" />
            <div className={`forgot-step ${forgotStep >= 3 ? 'active' : ''}`}>
              <span className="step-num">3</span>
              <span className="step-label">Reset</span>
            </div>
          </div>

          {forgotMessage && <div className="success-message">{forgotMessage}</div>}
          {forgotError && <div className="error-message">{forgotError}</div>}

          {/* Step 1: Enter username and email */}
          {forgotStep === 1 && (
            <form onSubmit={handleForgotStep1}>
              <div className="form-group">
                <label>Username</label>
                <input type="text" placeholder="Enter your username" value={forgotUsername} onChange={(e) => setForgotUsername(e.target.value)} autoFocus />
              </div>
              <div className="form-group">
                <label>Registered Email</label>
                <input type="email" placeholder="Enter your registered email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
              </div>
              <button type="submit" className="btn-login" disabled={forgotLoading}>
                {forgotLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
              <div className="login-footer-link">
                <button type="button" className="link-btn" onClick={resetForgotState}>Back to Login</button>
              </div>
            </form>
          )}

          {/* Step 2: Enter OTP */}
          {forgotStep === 2 && (
            <form onSubmit={handleForgotStep2}>
              <div className="form-group">
                <label>Verification Code</label>
                <input
                  type="text"
                  placeholder="Enter 6-digit code from email"
                  value={forgotOtp}
                  onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="otp-input"
                  autoFocus
                />
                <span className="form-hint-login">Check your email for the verification code. It expires in 10 minutes.</span>
              </div>
              <button type="submit" className="btn-login" disabled={forgotLoading}>
                {forgotLoading ? 'Verifying...' : 'Verify Code'}
              </button>
              <div className="login-footer-link">
                <button type="button" className="link-btn" onClick={() => { setForgotStep(1); setForgotError(''); setForgotMessage(''); }}>Resend Code</button>
              </div>
            </form>
          )}

          {/* Step 3: Set new password */}
          {forgotStep === 3 && (
            <form onSubmit={handleForgotStep3}>
              <div className="form-group">
                <label>New Password</label>
                <input type="password" placeholder="Enter new password" value={forgotNewPassword} onChange={(e) => setForgotNewPassword(e.target.value)} autoFocus />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input type="password" placeholder="Re-enter new password" value={forgotConfirmPassword} onChange={(e) => setForgotConfirmPassword(e.target.value)} />
              </div>
              <span className="form-hint-login">Min 8 characters with uppercase, lowercase, number, and special character.</span>
              <button type="submit" className="btn-login" style={{ marginTop: 16 }} disabled={forgotLoading}>
                {forgotLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // =============================================
  // MAIN LOGIN PAGE
  // =============================================
  return (
    <div className="login-container">
      <div className="login-card">
        {/* Tenant logo / selector */}
        {tenants.length > 0 && (
          <div className="login-tenant-area">
            {tenants.length > 1 ? (
              <select
                className="login-tenant-select"
                value={selectedTenant?.TenantID || ''}
                onChange={(e) => {
                  const t = tenants.find((t) => t.TenantID === parseInt(e.target.value));
                  setSelectedTenant(t);
                }}
              >
                {tenants.map((t) => (
                  <option key={t.TenantID} value={t.TenantID}>{t.InstituteName}</option>
                ))}
              </select>
            ) : null}
            {(selectedTenant?.LogoImageUrl || selectedTenant?.LogoUrl) ? (
              <img
                src={resolveBackendUrl(selectedTenant.LogoImageUrl) || selectedTenant.LogoUrl}
                alt={selectedTenant.Acronym || 'Logo'}
                className="login-logo-img"
              />
            ) : selectedTenant?.Acronym ? (
              <div className="login-logo-acronym">{selectedTenant.Acronym}</div>
            ) : null}
          </div>
        )}

        <div className="login-header">
          <h1>QPOSM</h1>
          <p>Question Paper & On-Screen Evaluation</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          {/* Math Captcha */}
          <div className="form-group">
            <label>Security Check</label>
            <div className="math-captcha-row">
              <div className="math-captcha-display">
                <span>{captchaDisplay || 'Loading...'}</span>
              </div>
              <input
                type="text"
                placeholder="Answer"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value.replace(/[^0-9-]/g, ''))}
                className="math-captcha-input"
                autoComplete="off"
              />
              <button type="button" className="captcha-refresh" onClick={loadCaptcha} title="New question">
                ↻
              </button>
            </div>
          </div>

          <button type="submit" className="btn-login" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="login-footer-link">
            <button type="button" className="link-btn" onClick={() => setShowForgot(true)}>Forgot Password?</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
